import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Download, Loader2, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useCart } from "../context/CartContext";
import { getPaymentStatus, API } from "../lib/api";
import Seo from "../components/Seo";

const PaymentSuccess = () => {
  const { t } = useLang();
  const { clear } = useCart();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("verifying");
  const [downloads, setDownloads] = useState([]);
  const [email, setEmail] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("pending");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (res.payment_status === "paid") {
          setDownloads(res.downloads || []);
          setEmail(res.email);
          setEmailSent(!!res.email_sent);
          setStatus("paid");
          clear();
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= 8) {
        if (!cancelled) setStatus("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="bg-obsidian min-h-screen">
      <Seo pageKey="shop" />
      <section className="mx-auto flex max-w-3xl flex-col items-center px-6 pt-48 pb-40 text-center md:px-12">
        {status === "verifying" && (
          <div data-testid="payment-verifying" className="flex flex-col items-center gap-6 text-white/70">
            <Loader2 size={40} className="animate-spin text-champagne" />
            <p className="text-base font-light">{t.payment.verifying}</p>
          </div>
        )}

        {status === "paid" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-champagne/40 text-champagne">
              <Check size={28} />
            </div>
            <span className="mt-8 block text-xs uppercase tracking-[0.3em] text-champagne">{t.payment.successOverline}</span>
            <h1 className="mt-6 font-serif text-4xl font-light leading-tight text-white md:text-6xl">{t.payment.successTitle}</h1>
            <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-white/60">{t.payment.successSub}</p>

            <div className="mx-auto mt-12 max-w-lg divide-y divide-white/10 border-y border-white/10" data-testid="download-list">
              {downloads.map((d) => (
                <a
                  key={d.token}
                  href={`${API}/payments/download/${d.token}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`download-${d.slug}`}
                  className="group flex items-center justify-between gap-4 py-5 text-left transition-colors hover:text-champagne"
                >
                  <span className="font-serif text-lg font-light text-white group-hover:text-champagne">{d.title}</span>
                  <span className="flex shrink-0 items-center gap-2 bg-champagne px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-obsidian transition-colors group-hover:bg-champagne-light">
                    <Download size={13} /> {t.payment.download}
                  </span>
                </a>
              ))}
            </div>

            {email && emailSent && (
              <p className="mt-8 text-xs text-white/40">{t.payment.emailNote.replace("{email}", email)}</p>
            )}
            {email && !emailSent && (
              <p className="mt-8 text-xs text-white/40">{t.payment.downloadHere}</p>
            )}

            <div className="mt-12 flex items-center justify-center gap-6">
              <Link to="/shop" data-testid="back-to-shop" className="text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-champagne">
                {t.payment.backShop}
              </Link>
              <Link to="/" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-champagne">
                {t.payment.home} <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        )}

        {status === "pending" && (
          <div data-testid="payment-pending" className="flex flex-col items-center gap-8 text-white/70">
            <p className="max-w-md text-base font-light leading-relaxed">{t.payment.pending}</p>
            <Link to="/shop" className="border border-champagne px-7 py-3 text-xs uppercase tracking-[0.2em] text-champagne">
              {t.payment.backShop}
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

export default PaymentSuccess;
